import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';
import FAQ from '../../components/ui/FAQ';
import { getFAQsForPage } from '../../utils/faqContent';
import { generatePageFAQSchema } from '../../utils/faqSchema';

const LandingPage = () => {
  const { user } = useAuth();
  
  // Get FAQs for landing page
  const landingFAQs = getFAQsForPage('landing');
  const faqSchema = generatePageFAQSchema('landing', 'https://mailvalidator.octalchip.com/landingpage', {
    pageTitle: 'EmailValidator - Professional Email Validation Service',
    pageDescription: 'Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists to reduce bounce rates and improve deliverability.'
  });

  const features = [
    {
      icon: 'Shield',
      title: '99.9% Accuracy',
      description: 'Advanced validation algorithms ensure high accuracy rates for reliable results.'
    },
    {
      icon: 'Zap',
      title: 'Real-time Validation',
      description: 'Instant validation results with comprehensive email health checks.'
    },
    {
      icon: 'Users',
      title: 'Bulk Processing',
      description: 'Validate thousands of emails simultaneously with our bulk processing tools.'
    },
    {
      icon: 'Code',
      title: 'API Access',
      description: 'Integrate email validation directly into your applications with our REST API.'
    },
    {
      icon: 'BarChart',
      title: 'Detailed Reports',
      description: 'Get comprehensive reports with validation status and deliverability insights.'
    },
    {
      icon: 'Lock',
      title: 'Secure & Private',
      description: 'Your data is protected with enterprise-grade security and privacy measures.'
    }
  ];

  const benefits = [
    'Reduce bounce rates by up to 95%',
    'Improve email deliverability',
    'Save money on email marketing costs',
    'Protect your sender reputation',
    'Comply with email regulations',
    'Boost campaign performance'
  ];

  const stats = [
    { number: '10M+', label: 'Emails Validated' },
    { number: '99.9%', label: 'Accuracy Rate' },
    { number: '500+', label: 'Happy Clients' },
    { number: '24/7', label: 'Support' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="EmailValidator - Professional Email Validation Service"
        description="Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists to reduce bounce rates and improve deliverability. Get 100 free credits - Start validating today!"
        keywords="email validator, email validation, email verification, bulk email validation, email checker, email validation API, email deliverability, bounce rate reduction, email marketing tools"
        canonical="https://mailvalidator.octalchip.com/landingpage"
        structuredData={faqSchema}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="relative py-20 bg-gradient-to-br from-gray-50 via-white to-primary-light">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center bg-primary-light text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Icon name="Star" size={16} className="mr-2" />
                  Professional Email Validation Service
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-900">
                  Ready to Improve Your
                  <span className="block text-primary">Email Marketing?</span>
                </h1>
                <p className="text-xl md:text-2xl mb-8 text-gray-600 leading-relaxed">
                  Join thousands of businesses that trust our email validation service to improve their deliverability and campaign performance.
                </p>
                
                {/* CTA Buttons - Only show for non-authenticated users */}
                {!user && (
                  <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <Link to="/">
                      <Button
                        variant="primary"
                        size="lg"
                        iconName="ArrowRight"
                        iconPosition="right"
                        animation="bounce"
                        className="shadow-lg"
                      >
                        Start Free Trial
                      </Button>
                    </Link>
                    <Link to="docs">
                      <Button
                        variant="outline"
                        size="lg"
                        iconName="ExternalLink"
                        iconPosition="right"
                        animation="slide"
                        className="bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                      >
                        View API Docs
                      </Button>
                    </Link>
                  </div>
                )}

                {/* Benefits */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={16} className="text-green-500 mr-2" />
                    <span className="text-gray-600 font-medium">100 Free Credits</span>
                  </div>
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={16} className="text-green-500 mr-2" />
                    <span className="text-gray-600 font-medium">No Credit Card</span>
                  </div>
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={16} className="text-green-500 mr-2" />
                    <span className="text-gray-600 font-medium">Cancel Anytime</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Mail" size={32} className="text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2 text-gray-900">Try Email Validation</h3>
                    <p className="text-gray-600">Validate a single email address instantly</p>
                  </div>
                  
                  <div className="space-y-4">
                    <input 
                      type="email" 
                      placeholder="Enter email address"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                    <Button
                      variant="primary"
                      size="md"
                      fullWidth
                      iconName="Search"
                      iconPosition="right"
                      animation="glow"
                    >
                      Validate Email
                    </Button>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500">
                      By validating, you agree to our{' '}
                      <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-16 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose EmailValidator?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional email validation technology designed to improve your email marketing results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow border border-gray-100">
                <div className="w-16 h-16 bg-primary-light rounded-lg flex items-center justify-center text-3xl mb-6">
                  <Icon name={feature.icon} size={32} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Transform Your Email Marketing
              </h2>
              <p className="text-xl text-gray-600">
                Stop losing money on invalid emails. Start improving your deliverability today.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                {benefits.slice(0, 3).map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <Icon name="CheckCircle" size={20} className="text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-900">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-4">
                {benefits.slice(3).map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <Icon name="CheckCircle" size={20} className="text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-900">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Only show for non-authenticated users */}
      {!user && (
        <section id="cta" className="py-20 bg-primary text-primary-foreground">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Improve Your Email Deliverability?
            </h2>
            <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
              Join thousands of businesses that trust our email validation service to improve their campaign performance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button
                  variant="secondary"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  animation="float"
                  className="bg-white text-primary hover:bg-gray-100 shadow-lg"
                >
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  variant="outline"
                  size="lg"
                  animation="wiggle"
                  className="border-2 border-white text-white hover:bg-white hover:text-primary"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about email validation and our service
              </p>
            </div>
            <FAQ items={landingFAQs} variant="default" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage; 